import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { debounce } from 'lodash';

import { HighlightOptions } from '~constants/maps';

import { HeaderCellProps, Worker } from '~weekly-planner/lib/common';

import ClickableIcon from '~components/ClickableIcon';
import { faCheck, faRoute, faX } from '~components/icon';
import { BiddingCard, TotalCard, WorkerCard } from '~weekly-planner/components/card';
import { WorkerAlerts, WorkerQualifications } from '~weekly-planner/components/common';

import { visualiseDistance } from '~weekly-planner/actions/visualise';

import { selectFocusedAppointment, selectFocusOptions, selectOptions } from '~weekly-planner/selectors';
import { selectFilteredSimulatedWorkers } from '~weekly-planner/selectors/simulate';

const WorkerCell: React.FC<HeaderCellProps> = ({ data, date, style, isDashboardView = false }) => {
  const worker = data as Worker;
  const { id, qualifications } = worker;

  const { highlightType } = useAppSelector(selectFocusOptions);
  const filteredWorkers = useAppSelector(selectFilteredSimulatedWorkers);
  const [lastActionResult, setLastActionResult] = useState<'success' | 'fail' | null>(null);

  const { workerAwardAlerts, workerQualifications, totalSimple, totalAppointments, totalPredicted, totalDistance } =
    useAppSelector(selectOptions);

  const focused = useAppSelector(selectFocusedAppointment);
  const { no, yes } = focused?.appointment?.client?.preferences ?? { no: [], yes: [] };

  const { isBiddingView } = focused || {};
  const events = Object.values(worker.events).flat(1);

  const dispatch = useAppDispatch();

  if (!date) return <></>;

  const renderTotals = () => {
    const hasTotals = totalSimple || totalAppointments || totalPredicted || totalDistance;
    if (events.length === 0 || !hasTotals) return <></>;
    return (
      <div>
        <div className="total-row">
          {totalSimple && <TotalCard type="simple" events={events} direction="right" />}
          {totalAppointments && <TotalCard type="appointments" events={events} direction="right" />}
          {totalPredicted && <TotalCard type="predicted" events={events} direction="right" />}
          {totalDistance && <TotalCard type="distance" events={events} direction="right" />}
        </div>
      </div>
    );
  };

  const visualiseWorkerTravel = useCallback(
    debounce(async () => {
      const {
        payload: { success },
      } = await dispatch(
        visualiseDistance({
          worker,
          appointment: focused.appointment,
        }),
      );

      setLastActionResult(success ? 'success' : 'fail');
      setTimeout(() => setLastActionResult(null), 3000);
    }, 500),
    [dispatch, worker, focused?.appointment],
  );

  const renderWorkerVisualizeIcon = () => {
    if (filteredWorkers.length === 0) return <></>;
    const filteredUser = filteredWorkers.some(({ id: workerId }) => workerId === id);
    if (!filteredUser) return <></>;

    let icon;
    let color;
    switch (lastActionResult) {
      case 'success':
        icon = faCheck;
        color = 'green';
        break;
      case 'fail':
        icon = faX;
        color = 'red';
        break;
      default:
        icon = faRoute;
        color = 'black';
    }

    return (
      <>
        <ClickableIcon
          id={`${id}-visualize-icon`}
          icon={icon}
          onClick={visualiseWorkerTravel}
          color={color}
          toolTip="Visualise travel to appointment"
        />
      </>
    );
  };

  const isPreferred = yes?.includes(Number(id)) ?? false;
  const isNotPreferred = no?.includes(Number(id)) ?? false;

  return (
    <div className="worker-cell" style={style}>
      <div className="d-flex flex-column justify-content-between w-100">
        <div className="worker-info">
          {highlightType === HighlightOptions.DISTANCE && (
            <div className="visualise">{renderWorkerVisualizeIcon()}</div>
          )}
          <div className="px-2 mb-2">
            <WorkerCard
              worker={worker}
              date={date}
              isDashboardView={isDashboardView}
              isPreferred={isPreferred}
              isNotPreferred={isNotPreferred}
            />
          </div>
          <div className="px-2">
            {workerQualifications && <WorkerQualifications qualifications={qualifications} isCondensed />}
          </div>
        </div>
        <div>
          {workerAwardAlerts && (
            <WorkerAlerts
              worker={worker}
              events={events}
              className={events.length === 0 ? 'rounded-left' : undefined}
            />
          )}
          {renderTotals()}
        </div>
      </div>
      {isBiddingView && <BiddingCard workerId={id} />}
    </div>
  );
};

export default WorkerCell;
